import {
  CmsCareersConfigData,
  isDevelopment,
  Locales,
} from 'src/lib/constants';
import { marketingCmsClient } from 'src/lib/contentful-client';
import Ashby, { Job, Jobs } from 'src/lib/ashby-client';
import { strStartsWithVowel, toKebabCase } from 'src/lib/utils';
import type {
  ICareerSearchJob,
  ICareersSearchConfigData,
  ICareersSearchJob,
  ICleanJobProps,
  IJobsDataStore,
} from './CareersSearch.types';

/**
 * Builds the canonical detail-page path segments for a posting:
 * - multi-location -> `{department}/{slug}` (no location segment)
 * - single-location -> `{department}/{location}/{slug}`
 * Returned as an array so it maps directly onto the `[...slug]` catch-all route.
 */
export const getCanonicalPathParts = (job: Job, slug: string): string[] => {
  const department = toKebabCase(job.categories.team);
  const allLocations = job.categories.allLocations;
  return allLocations.length > 1
    ? [department, slug]
    : [department, toKebabCase(allLocations[0]), slug];
};

const isValidJob = (job: Job): boolean => {
  return !!(
    job?.categories?.team &&
    job?.categories?.allLocations?.length &&
    job.text
  );
};

/**
 * Assigns a unique URL slug to every posting. Slugs are made unique *within a
 * department* (the department is part of the cache key / URL), so two postings
 * never collide. A posting takes the first free slug from `title`, `title-2`,
 * `title-3`, ...; the oldest posting (by createdAt, then id) wins the bare
 * `title`. Checking against already-used slugs - not just same-title siblings -
 * also prevents a `-2` suffix from colliding with a posting literally titled
 * "Title 2".
 */
export const getJobSlugs = (jobs: Job[]): Map<string, string> => {
  const ordered = jobs.filter(isValidJob).sort((jobA, jobB) => {
    return (
      toKebabCase(jobA.categories.team).localeCompare(
        toKebabCase(jobB.categories.team),
      ) ||
      jobA.createdAt - jobB.createdAt ||
      jobA.id.localeCompare(jobB.id)
    );
  });

  const usedByDepartment = new Map<string, Set<string>>();
  const slugs = new Map<string, string>();
  ordered.forEach((job) => {
    const department = toKebabCase(job.categories.team);
    const used = usedByDepartment.get(department) ?? new Set<string>();
    usedByDepartment.set(department, used);

    const base = toKebabCase(job.text);
    let slug = base;
    let suffix = 2;
    while (used.has(slug)) {
      slug = `${base}-${suffix}`;
      suffix += 1;
    }
    used.add(slug);
    slugs.set(job.id, slug);
  });
  return slugs;
};

/**
 * Builds a posting summary for the jobs list and "Other opportunities"
 * sections. The key doubles as the canonical detail page path, e.g.
 * `engineering/staff-software-engineer` (multi-location) or
 * `enterprise/san-francisco-hq/account-executive` (single-location).
 */
const makeJobSummary = (
  job: Job,
  slugs: Map<string, string>,
): ICareersSearchJob => {
  return {
    key: getCanonicalPathParts(job, slugs.get(job.id)).join('/'),
    id: job.id,
    title: job.text,
    locations: job.categories.allLocations,
    department: job.categories.team,
  };
};

/**
 * Loads jobs and converts them to a simpler format for the Jobs List.
 * Returns one entry per posting (locations are consolidated, not expanded).
 */
export const loadJobsDataForPlaceholder = async () => {
  const leverJobs = await Ashby.api.getJobs();
  const slugs = getJobSlugs(leverJobs ?? []);
  // cleanup and only return needed fields for the jobs list component (filters and search)
  return (leverJobs ?? [])
    .filter(isValidJob)
    .map((job) => {
      return makeJobSummary(job, slugs);
    })
    .sort((jobA, jobB) => {
      return jobA.key.localeCompare(jobB.key);
    });
};

/**
 * Gets the settings for carrers from a contentful data store
 */
export const getCareersSettings = async (
  entryId?: string,
): Promise<ICareersSearchConfigData | undefined> => {
  const dataStore = await marketingCmsClient.getEntry<IJobsDataStore>(
    entryId ||
      (isDevelopment ? CmsCareersConfigData.DEV : CmsCareersConfigData.PROD),
  );
  return dataStore?.fields?.data ?? {};
};

/**
 * Get's a department description from
 * @param department
 */
export const getDepartmentDescription = async (department) => {
  if (!department) return '';
  try {
    const config = await getCareersSettings();
    if (config) {
      return config?.departments?.[department]?.description ?? '';
    }
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.error('Error loading careers settings.', e?.toString());
    return '';
  }
};

/**
 * Payment interval values that map from Lever
 */
export const PAYMENT_INTERVAL_MAP = {
  'per-year-salary': 'per year',
  'per-month-salary': 'per month',
  'semi-month-salary': 'twice a month',
  'bi-month-salary': 'every two months',
  'bi-week-salary': 'every 2 weeks',
  'per-week-salary': 'a week',
  'per-day-wage': 'per day',
  'per-hour-wage': 'an hour',
  'one-time': '',
};

/**
 * Converts a job salary range object into a salaray range string.
 */
export const makeSalaryRange = ({
  salaryRange,
  locale = Locales.EN_US,
}): string => {
  if (!salaryRange) {
    return '';
  }

  const currency = new Intl.NumberFormat(locale || 'en-US', {
    style: 'currency',
    currency: salaryRange?.currency || 'USD',
  });

  const min = salaryRange.min ?? 0;
  const max = salaryRange.max ?? 0;

  // if we dont have any salary numbers, return
  if (min <= 0 && max <= 0) {
    return '';
  }

  const interval = PAYMENT_INTERVAL_MAP[salaryRange.interval] ?? '';

  // if we have a salary range, create a range, otherwise, use the larger value
  if (min > 0 && max > 0 && min < max) {
    return `${currency.format(salaryRange.min)} - ${currency.format(
      salaryRange.max,
    )} ${interval}`.trim();
  }

  return `${currency.format(Math.max(min, max))} ${interval}`.trim();
};

/**
 * Get a location name from list of locations from a kebab cased location.
 */
export const getLocationFromLocations = (
  location: string,
  locations: string[],
): string => {
  return locations.find((l) => {
    return toKebabCase(l) === location;
  });
};

/**
 * Cleans up and only passes needed information to jobs details
 */
export const cleanJobDetails = ({
  job,
  location,
}: ICleanJobProps): ICareerSearchJob => {
  const createdDate = new Date(job.createdAt);
  const datePosted =
    createdDate.getFullYear() +
    '-' +
    (createdDate.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    createdDate.getDate().toString().padStart(2, '0');

  const allLocations = job.categories.allLocations ?? [];

  return {
    id: job.id,
    text: job.text,
    department: job.categories.team,
    // a specific location for the per-location pages, all locations for the
    // consolidated page
    location: location
      ? getLocationFromLocations(location, allLocations)
      : allLocations.join(', '),
    locations: allLocations,
    commitment: job.categories.commitment ?? null,
    description: job.description ? stripStyleAttr(job.description) : '',
    lists: job.lists.map((list) => {
      return {
        text: list.text,
        content: stripStyleAttr(list.content),
      };
    }),
    additional: job.additional ? stripStyleAttr(job.additional) : '',
    salary: job.salaryRange
      ? makeSalaryRange({ salaryRange: job.salaryRange })
      : '',
    salaryDescription: job.salaryDescription
      ? stripStyleAttr(job.salaryDescription)
      : '',
    applyUrl: job.applyUrl,
    datePosted: datePosted,
  };
};

/**
 * Builds the jobs cache and returns the paths for the `[...slug]` job detail
 * route. URL scheme:
 * - multi-location -> one canonical `{dept}/{slug}` page (the URL listed on
 *   plaid.com) plus one `{dept}/{location}/{slug}` page per location, kept for
 *   SEO with a canonical link back to the canonical page
 * - single-location -> one canonical `{dept}/{location}/{slug}` page
 * Cache keys are the joined path so the page lookup is `params.slug.join('/')`.
 */
export const getJobDetailPathsAndBuildCache = async (
  jobs: Job[] | null | undefined = undefined,
) => {
  if (!jobs) {
    jobs = await Ashby.api.getJobs();
  }

  const validJobs = jobs.filter(isValidJob);
  const slugs = getJobSlugs(validJobs);
  const massagedJobs: Jobs = {};

  // canonical summaries used for the "Other opportunities" sections
  const summaries = validJobs
    .map((job) => {
      return makeJobSummary(job, slugs);
    })
    .sort((jobA, jobB) => {
      return jobA.key.localeCompare(jobB.key);
    });

  const paths: { params: { slug: string[] } }[] = [];

  const addPage = (
    parts: string[],
    job: Job,
    otherJobs: ICareersSearchJob[],
  ) => {
    const key = parts.join('/');
    // filter out duplicates (this can happen in cases where a multi-location job is added multiple times to different locations)
    if (massagedJobs[key]) {
      return;
    }
    massagedJobs[key] = { ...job, otherJobs };
    paths.push({ params: { slug: parts } });
  };

  validJobs.forEach((job) => {
    const slug = slugs.get(job.id);
    const department = toKebabCase(job.categories.team);
    const isMultiLocation = job.categories.allLocations.length > 1;
    const otherJobs = summaries.filter((summary) => {
      return (
        summary.id !== job.id && summary.department === job.categories.team
      );
    });

    // canonical page
    addPage(getCanonicalPathParts(job, slug), job, otherJobs);

    // per-location pages for multi-location postings (SEO; canonical -> above)
    if (isMultiLocation) {
      job.categories.allLocations.forEach((location) => {
        addPage(
          [department, toKebabCase(location), slug],
          { ...job, categories: { ...job.categories, location } },
          otherJobs,
        );
      });
    }
  });

  // save the massaged jobs data to the cache for fast lookup when building pages.
  await Ashby.cache.set(massagedJobs);

  return paths;
};

/**
 * Builds the sanitized job + MetaTags props for a detail page, given the
 * `[...slug]` path parts. Handles both URL shapes:
 * - `[dept, slug]`        -> multi-location canonical page (lists all locations)
 * - `[dept, location, slug]` -> a single location. If the posting is
 *   multi-location this is an SEO page that canonicals back to `{dept}/{slug}`;
 *   if single-location it is itself canonical.
 */
export const buildJobDetailMetaData = async ({
  job,
  slugParts,
  intro,
}: {
  job: Job;
  slugParts: string[];
  intro: string;
}) => {
  const { team } = job.categories || {};
  const isLocationPage = slugParts.length >= 3;
  const location = isLocationPage ? slugParts[1] : undefined;
  const sanitizedJob = cleanJobDetails({ job, location });
  const isMultiLocation = sanitizedJob.locations.length > 1;

  const metaLocation =
    !isLocationPage && isMultiLocation
      ? 'Multiple Locations'
      : sanitizedJob.location;
  const metaDescription = isLocationPage
    ? `Join Plaid's ${team} team in ${strStartsWithVowel(
        sanitizedJob.location,
      )} ${sanitizedJob.location} based ${sanitizedJob.text} role. ${intro}`
    : `Join Plaid's ${team} team as ${strStartsWithVowel(sanitizedJob.text)} ${
        sanitizedJob.text
      } in ${sanitizedJob.location}. ${intro}`;
  // multi-location per-location pages canonical back to the location-less URL
  const canonicalPagePath =
    isLocationPage && isMultiLocation
      ? `/careers/openings/${slugParts[0]}/${slugParts[2]}/`
      : undefined;
  const jsonLdLocations = isLocationPage
    ? [sanitizedJob.location]
    : sanitizedJob.locations;

  const metaData = {
    'meta-title': `${sanitizedJob.text} | ${metaLocation}`,
    'meta-description': metaDescription,
    ...(canonicalPagePath ? { canonicalPagePath } : {}),
    structuredData: {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      hiringOrganization: {
        '@type': 'Organization',
        name: 'Plaid',
        sameAs: 'https://plaid.com/',
      },
      title: sanitizedJob.text,
      description: sanitizedJob.description || '',
      datePosted: sanitizedJob.datePosted,
      jobLocation: jsonLdLocations.map((loc) => {
        return {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: loc,
            addressCountry: job.country,
          },
        };
      }),
      identifier: {
        '@type': 'PropertyValue',
        name: 'ID',
        value: job.id,
      },
      ...(job.salaryRange && {
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: job.salaryRange?.currency || 'USD',
          value: {
            '@type': 'QuantitativeValue',
            minValue: job.salaryRange.min || 0,
            maxValue: job.salaryRange.max || 0,
            unitText: 'YEAR',
          },
        },
      }),
    },
  };

  return { sanitizedJob, metaData };
};

/**
 * Strips styling tags from lever html detail.
 */
function stripStyleAttr(string) {
  const styleRegex = new RegExp(' style="[^"]*"', 'ig');
  return string.replace(styleRegex, () => {
    return '';
  });
}
